import { Link } from "react-router-dom";

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      <div className="max-w-[800px] mx-auto px-6 lg:px-8 py-16 sm:py-20">
        <h1 className="text-[32px] sm:text-[40px] font-normal text-[#202124] leading-tight mb-2">Terms of Service</h1>
        <p className="text-[13px] text-[#80868b] mb-10">Last updated: March 29, 2026</p>

        <div className="prose-eamico space-y-8 text-[15px] text-[#3c4043] leading-relaxed">

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">1. Agreement to Terms</h2>
            <p>
              These Terms of Service ("Terms") constitute a legally binding agreement between you ("you" or "User") and Eamico Inc. ("Eamico," "we," "us," or "our"), a Federal Canadian corporation (Corporation Number 1781131-4, Business Number 792360372), and a subsidiary of Aikaro Technologies Corp.
            </p>
            <p className="mt-3">
              By accessing or using our website, products (Eamico Studio and Eamico Cloud), or services (collectively, the "Services"), you agree to be bound by these Terms. If you do not agree to these Terms, you must not access or use the Services.
            </p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">2. Eligibility</h2>
            <p>You must be at least 18 years of age (or the age of majority in your jurisdiction) to use the Services. By using the Services, you represent and warrant that you meet this eligibility requirement. If you are using the Services on behalf of an organization, you represent and warrant that you have authority to bind that organization to these Terms.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">3. Description of Services</h2>
            <p>Eamico provides cloud-based development and hosting services, including:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1.5">
              <li><strong>Eamico Studio:</strong> A cloud-native integrated development environment (IDE) for writing, testing, and deploying code.</li>
              <li><strong>Eamico Cloud:</strong> An edge hosting platform powered by the Aethernet for deploying and scaling applications.</li>
            </ul>
            <p className="mt-3">Services are currently in development. Features, availability, and pricing are subject to change without notice during the pre-launch and beta phases.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">4. Waitlist & Pre-Launch</h2>
            <p>By joining our waitlist, you acknowledge that:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1.5">
              <li>Joining the waitlist does not guarantee access to the Services or any specific launch date</li>
              <li>We may use your email address to send you product updates, launch notifications, and related communications</li>
              <li>You may unsubscribe from communications at any time by contacting us</li>
              <li>Waitlist position does not create any contractual obligation on our part</li>
            </ul>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">5. User Accounts</h2>
            <p>When account functionality becomes available:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1.5">
              <li>You are responsible for maintaining the confidentiality of your account credentials</li>
              <li>You are responsible for all activities that occur under your account</li>
              <li>You must provide accurate, current, and complete information during registration</li>
              <li>You must notify us immediately of any unauthorized access to your account</li>
              <li>We reserve the right to suspend or terminate accounts that violate these Terms</li>
            </ul>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">6. Acceptable Use</h2>
            <p>You agree not to use the Services to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1.5">
              <li>Violate any applicable law, regulation, or third-party rights</li>
              <li>Transmit malware, viruses, or other harmful code</li>
              <li>Engage in unauthorized access to any system, network, or data</li>
              <li>Send unsolicited communications (spam) or engage in phishing</li>
              <li>Interfere with or disrupt the integrity or performance of the Services</li>
              <li>Attempt to reverse engineer, decompile, or disassemble the Services</li>
              <li>Use the Services for cryptocurrency mining without authorization</li>
              <li>Store, transmit, or process illegal content</li>
              <li>Resell or redistribute the Services without our written consent</li>
            </ul>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">7. Intellectual Property</h2>
            <p>
              The Services, including all content, features, functionality, software, code, designs, trademarks, and documentation, are owned by Eamico Inc. and/or its licensors and are protected by Canadian, United States, and international intellectual property laws.
            </p>
            <p className="mt-3">
              You retain ownership of any content you create, upload, or store using the Services. By using the Services, you grant us a limited, non-exclusive license to host, store, and process your content solely for the purpose of providing the Services to you.
            </p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">8. Payment Terms</h2>
            <p>When paid Services become available:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1.5">
              <li>Fees are as described on our pricing page and are subject to change with notice</li>
              <li>All fees are in United States Dollars (USD) unless otherwise specified</li>
              <li>Applicable taxes (including Canadian GST/HST and U.S. sales tax) will be added as required</li>
              <li>Subscription fees are billed in advance on a recurring basis</li>
              <li>Refunds are handled in accordance with our refund policy</li>
            </ul>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">9. Disclaimer of Warranties</h2>
            <p>
              THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE. TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, WE DISCLAIM ALL WARRANTIES, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND QUIET ENJOYMENT.
            </p>
            <p className="mt-3">
              WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, SECURE, OR FREE OF HARMFUL COMPONENTS. YOUR USE OF THE SERVICES IS AT YOUR SOLE RISK.
            </p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">10. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL EAMICO INC., AIKARO TECHNOLOGIES CORP., OR THEIR RESPECTIVE OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR USE, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICES.
            </p>
            <p className="mt-3">
              OUR TOTAL AGGREGATE LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICES SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNTS PAID BY YOU TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED CANADIAN DOLLARS (CAD $100).
            </p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">11. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Eamico Inc., Aikaro Technologies Corp., and their respective officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, costs, and expenses (including reasonable attorneys' fees) arising out of or related to your use of the Services, violation of these Terms, or violation of any rights of a third party.
            </p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">12. Governing Law & Dispute Resolution</h2>
            <p>
              These Terms are governed by and construed in accordance with the federal laws of Canada and the laws of the Province of Ontario, without regard to conflict of law principles.
            </p>
            <p className="mt-3">
              Any disputes arising out of or relating to these Terms shall first be submitted to good-faith mediation. If mediation is unsuccessful, disputes shall be resolved by binding arbitration in accordance with the rules of the ADR Institute of Canada, conducted in English in Ontario, Canada.
            </p>
            <p className="mt-3">
              For U.S. residents: You agree to waive any right to participate in a class action lawsuit or class-wide arbitration against Eamico Inc., to the extent permitted by applicable law.
            </p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">13. Termination</h2>
            <p>
              We may terminate or suspend your access to the Services at any time, with or without cause, with or without notice. Upon termination, your right to use the Services ceases immediately. Provisions that by their nature should survive termination shall survive, including intellectual property, warranty disclaimers, indemnification, and limitations of liability.
            </p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">14. Modifications to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. Material changes will be communicated by posting the updated Terms on our website and updating the "Last updated" date. Your continued use of the Services after any modification constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">15. Severability</h2>
            <p>
              If any provision of these Terms is held to be invalid or unenforceable, that provision shall be enforced to the maximum extent permissible, and the remaining provisions shall remain in full force and effect.
            </p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">16. Entire Agreement</h2>
            <p>
              These Terms, together with our <Link to="/privacy" className="text-[#ff9a4d] hover:underline">Privacy Policy</Link> and <Link to="/cookies" className="text-[#ff9a4d] hover:underline">Cookie Policy</Link>, constitute the entire agreement between you and Eamico Inc. regarding the Services and supersede all prior agreements and understandings.
            </p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">17. Contact Us</h2>
            <div className="bg-[#f8f9fa] rounded-xl border border-[#e8eaed] p-6 mt-3">
              <p className="font-medium text-[#202124] mb-3">Eamico Inc.</p>
              <p>Subsidiary of Aikaro Technologies Corp.</p>
              <p>Federal Canadian Corporation</p>
              <p>Corporation Number: 1781131-4</p>
              <p>Business Number: 792360372</p>
              <div className="mt-4 space-y-1">
                <p><strong>Legal Inquiries:</strong> <a href="mailto:legal@aikarotech.com" className="text-[#ff9a4d] hover:underline">legal@aikarotech.com</a></p>
                <p><strong>Privacy Inquiries:</strong> <a href="mailto:privacy@aikarotech.com" className="text-[#ff9a4d] hover:underline">privacy@aikarotech.com</a></p>
              </div>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TermsPage;
